/**
 * The Add to mailshot box's price limit: a top price a person can TYPE, and what the box says of it
 * (Oct 2026; Lee scraped £1m to £1.25m to mail the homes up to £1.1m, and the box could only apply
 * its own figure or nothing, and did not say how many homes it left out for price).
 *
 * ⚠️ WHERE THE BOX'S OWN FIGURE COMES FROM. `config/settings`, `pricing.baseMaxVal`, in the TOOLS
 * project: the copy of the settings behind the administrators' Config page. The live quote journey
 * reads its own project's settings, so the two share a name and can differ. So the box says
 * "ClozeSure's own maximum as Tools holds it", and nothing about what the journey does above it.
 * This file never writes that setting, and nothing here could.
 *
 * ⚠️ THE TEST IS NOT CHANGED: `!price || price <= figure`, the same in the box and in the add
 * function (`addPropertiesToMailshot`, which applies whatever figure the PAGE sends, unchecked).
 * So: up to and including; a home with no price passes; and a price held as text that is not a
 * number ("£1,050,000") fails the test and is left out while the limit is on. The box counts those
 * apart and says so. `keepsForPrice` is that test, once, for the box's two lists.
 *
 * ⚠️ THE PAGE IS THE ONLY GATE on what figure is sent: `toSend` gives a whole number of pounds above
 * nought, or nothing. A figure that is not one is refused in words and the add is not offered.
 */
import { onePerHome } from './developmentChoice';

const n = (v) => Number(v || 0).toLocaleString('en-GB');
const list = (v) => (Array.isArray(v) ? v : []);
const homes = (count) => (Number(count) === 1 ? 'home' : 'homes');

/** A figure in pounds with its thousands: £1,100,000. */
export const pounds = (value) => `£${Number(value).toLocaleString('en-GB')}`;
/** The same without the sign, for the field. */
export const figureText = (value) => Number(value).toLocaleString('en-GB');

/** Nine digits: up to £999,999,999. A longer figure is a slip of the hand, not a price. */
export const MAX_DIGITS = 9;
export const OWN = "ClozeSure's own maximum as Tools holds it";

/**
 * What was typed, read as whole pounds. Strict on purpose: a wrong figure is worse than a refusal.
 * Taken: digits ("1100000"), digits with their thousands ("1,100,000"), either after a pound sign.
 * Refused: nothing typed, nought, a leading nought, pence or a decimal point, a letter ("1.1m"),
 * a minus, a space inside, commas that are not thousands ("110,0000"), more than nine digits.
 * @returns {{ ok: true, value: number } | { ok: false, why: 'empty'|'not-whole'|'too-large' }}
 */
export function parsePounds(typed) {
  const raw = String(typed === null || typed === undefined ? '' : typed).trim().replace(/^£\s*/, '');
  if (raw === '') return { ok: false, why: 'empty' };
  let digits = null;
  if (/^[1-9]\d*$/.test(raw)) digits = raw;
  else if (/^[1-9]\d{0,2}(,\d{3})+$/.test(raw)) digits = raw.split(',').join('');
  if (digits === null) return { ok: false, why: 'not-whole' };
  if (digits.length > MAX_DIGITS) return { ok: false, why: 'too-large' };
  return { ok: true, value: Number(digits) };
}

/**
 * The box's own figure, from the setting as it is held.
 * `held`: the setting has a value at all. `value`: that value as whole pounds, or null when it
 * has none or cannot be read as whole pounds (then the limit opens ON and refused: nothing is
 * added with no limit behind a setting that was there and could not be read).
 */
export function ownFigure(setting) {
  if (!setting) return { held: false, value: null, raw: '' };
  let read = { ok: false };
  if (typeof setting === 'number') read = Number.isInteger(setting) && setting > 0 && String(setting).length <= MAX_DIGITS ? { ok: true, value: setting } : { ok: false };
  else if (typeof setting === 'string') read = parsePounds(setting);
  return { held: true, value: read.ok ? read.value : null, raw: String(setting) };
}

/** What the field shows before anyone has typed in it. */
export const openingText = (own) => (own.value !== null ? figureText(own.value) : own.held ? own.raw : '');

/**
 * The limit as the box holds it now.
 * @returns {{ on: boolean, ok: boolean, figure: number|null, why?: string, typed: string }}
 *   `figure` is a whole number of pounds above nought, or null (off, or refused).
 */
export function limitOf(on, typed) {
  const text = String(typed === null || typed === undefined ? '' : typed);
  if (!on) return { on: false, ok: true, figure: null, typed: text };
  const read = parsePounds(text);
  return read.ok ? { on: true, ok: true, figure: read.value, typed: text } : { on: true, ok: false, figure: null, why: read.why, typed: text };
}

/** What the add function is sent: a whole number of pounds above nought, or nothing. */
export function toSend(limit) {
  const figure = limit && limit.on && limit.ok && Number.isInteger(limit.figure) && limit.figure > 0 ? limit.figure : null;
  return { applyMaxPrice: figure !== null, maxPrice: figure };
}

/** THE test, as it has always been and as the add function makes it. No figure: every home is kept. */
export const keepsForPrice = (home, figure) => figure === null || figure === undefined || !home.price || home.price <= figure;

export const NO_PRICE = 'none';
export const UNREADABLE = 'unreadable';
export const PRICED = 'priced';
/** A home's price as the test reads it: none (it passes), a number, or something that is not one. */
export function priceKind(home) {
  const price = home ? home.price : undefined;
  if (!price) return NO_PRICE;
  return Number.isNaN(Number(price)) ? UNREADABLE : PRICED;
}

/**
 * The homes in hand against a figure, each HOME once. `over`: priced above it. `unreadable`: a price
 * that is not a number (left out while a limit is on). `none`: no price (they pass).
 * With no figure nothing is left out for price, and `over` is 0.
 */
export function priceReading(properties, figure) {
  const each = onePerHome(list(properties));
  const reading = { homes: each.length, none: 0, unreadable: 0, over: 0, within: 0, leftOut: 0 };
  const limited = figure !== null && figure !== undefined;
  each.forEach((home) => {
    const kind = priceKind(home);
    if (kind === NO_PRICE) reading.none += 1;
    else if (kind === UNREADABLE) reading.unreadable += 1;
    else if (limited && !(home.price <= figure)) reading.over += 1;
    else reading.within += 1;
  });
  reading.leftOut = limited ? reading.over + reading.unreadable : 0;
  return reading;
}

const refusedWords = (limit) => {
  const fix = 'Type the figure in whole pounds, such as 1,100,000, or untick the limit. Nothing can be added until it is put right.';
  if (limit.why === 'empty') return `No top price is typed. ${fix}`;
  if (limit.why === 'too-large') return `"${limit.typed.trim()}" is too large to be a price. ${fix}`;
  return `"${limit.typed.trim()}" is not a whole number of pounds above nought. ${fix}`;
};

/**
 * What the box says of the limit.
 * @param {Object} given `limit` (limitOf), `own` (ownFigure), `hadRecords` (the box was given records, not ids),
 *   `inHand` (the homes the status and mailing-rule choices keep, BEFORE the price limit), `idsCount`.
 */
export function priceWords({ limit, own, hadRecords, inHand, idsCount }) {
  const words = { hint: '', refused: null, upTo: null, differs: null, differsKind: null, unreadable: null, off: null, reading: null };
  words.hint = own.value !== null
    ? `(On by default, at ${OWN}: ${pounds(own.value)})`
    : own.held
      ? `(Tools holds a ClozeSure maximum that could not be read as whole pounds: "${own.raw}")`
      : '(Tools holds no ClozeSure maximum: no limit unless you tick this and type one)';

  if (limit.on && !limit.ok) { words.refused = refusedWords(limit); return words; }

  if (!limit.on) {
    if (hadRecords && own.value !== null) {
      const above = priceReading(inHand, own.value).over;
      if (above > 0) words.off = `No price limit: ${n(above)} of these homes ${above === 1 ? 'is' : 'are'} priced above ${OWN} (${pounds(own.value)}) and WILL be added.`;
    }
    return words;
  }

  const reading = hadRecords ? priceReading(inHand, limit.figure) : null;
  words.reading = reading;
  if (!hadRecords) {
    const count = Number(idsCount || 0);
    words.upTo = `Up to and including ${pounds(limit.figure)}. ${count === 1 ? 'This home was' : `These ${n(count)} homes were`} given to this box by id only, so it cannot count what the limit leaves out: the add applies it, and the message after the add says how many it left out.`;
  } else if (reading.homes === 1) {
    words.upTo = `Up to and including ${pounds(limit.figure)}. This home is ${reading.over === 1 ? 'priced above it and is left out' : 'not priced above it'}.`;
  } else if (reading.over === 0) {
    words.upTo = `Up to and including ${pounds(limit.figure)}. None of these ${n(reading.homes)} homes is priced above it.`;
  } else {
    words.upTo = `Up to and including ${pounds(limit.figure)}. ${n(reading.over)} of these ${n(reading.homes)} homes ${reading.over === 1 ? 'is' : 'are'} priced above it and ${reading.over === 1 ? 'is' : 'are'} left out.`;
  }
  if (reading && reading.unreadable > 0) {
    words.unreadable = `${n(reading.unreadable)} ${reading.unreadable === 1 ? 'has' : 'have'} a price that could not be read as a number: left out while the limit is on.`;
  }
  if (own.value !== null && limit.figure !== own.value) {
    words.differsKind = limit.figure > own.value ? 'above' : 'below';
    words.differs = `${pounds(limit.figure)} is ${words.differsKind} ${OWN} (${pounds(own.value)}).`;
  }
  return words;
}

/**
 * How many homes the ADD FUNCTION left out for price, from its own answer: `excluded` is the homes
 * that failed its filters (the mailing rule and the price), `excludedByReason` those the mailing
 * rule held back. The difference is the price. null when the answer does not say (cannot tell).
 */
export function leftOutByAdd(data) {
  if (!data || !Number.isInteger(data.excluded) || data.excluded < 0) return null;
  const byRule = data.excludedByReason;
  if (!byRule || typeof byRule !== 'object' || Array.isArray(byRule)) return null;
  const held = Object.values(byRule).reduce((sum, v) => sum + (Number.isInteger(v) && v > 0 ? v : 0), 0);
  return Math.max(0, data.excluded - held);
}

/**
 * What the message after an add says of the price limit. Empty when there is nothing to say: a limit
 * left at the box's own figure that left nothing out reads exactly as it did before.
 * @param {Object} given `limit`, `own`, `hadRecords`, `inHand` as for priceWords; `byAdd`: leftOutByAdd of the answer.
 */
export function priceNote({ limit, own, hadRecords, inHand, byAdd }) {
  if (!limit.on) {
    if (own.value === null) return '';
    const above = hadRecords ? priceReading(inHand, own.value).over : 0;
    return `the price limit was UNTICKED${above > 0 ? `: ${n(above)} ${homes(above)} priced above ${OWN} (${pounds(own.value)}) ${above === 1 ? 'was' : 'were'} not left out for price` : ''}`;
  }
  if (!limit.ok) return '';
  const parts = [];
  const at = `above ${pounds(limit.figure)}`;
  if (hadRecords) {
    const reading = priceReading(inHand, limit.figure);
    if (reading.over > 0) parts.push(`${n(reading.over)} left out for price (${at})`);
    if (reading.unreadable > 0) parts.push(`${n(reading.unreadable)} left out with a price that could not be read as a number`);
    if (byAdd > 0) parts.push(`the add left out ${n(byAdd)} more for price`);
  } else if (byAdd === null || byAdd === undefined) {
    parts.push(`the add did not say how many it left out for price (${at})`);
  } else {
    parts.push(`the add left out ${byAdd === 0 ? 'none' : n(byAdd)} for price (${at})`);
  }
  if (own.value !== null && limit.figure !== own.value) parts.push(`the limit was typed as ${pounds(limit.figure)}, ${limit.figure > own.value ? 'above' : 'below'} ${OWN} (${pounds(own.value)})`);
  if (own.value === null) parts.push(`the limit was typed as ${pounds(limit.figure)}`);
  return parts.join('; ');
}
